// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateArtifact } from '../services/sopFactoryService';
import { fromProcedureDetail } from './fromProcedureDetail';
import { toProcedureDetail } from './toProcedureDetail';
import type { Artifact, ProcedureDetail } from '../types/procedures';

export interface SaveProcedureView {
  save: (procedure: ProcedureDetail, version: string) => Promise<ProcedureDetail>;
  isSaving: boolean;
  error: string | null;
}

/**
 * Wraps `updateArtifact()` (no backend route yet — see that function's own
 * docstring) with the `ProcedureDetail`<->`Artifact` mapping and cache
 * invalidation, so `ProcedureViewPage` doesn't need to know about either.
 * `version` is passed separately from `procedure` because the footer's
 * version-bump choice isn't part of the editable form state.
 */
export function useSaveProcedure(artifact: Artifact | null): SaveProcedureView {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ procedure, version }: { procedure: ProcedureDetail; version: string }) => {
      if (!artifact) throw new Error('No procedure loaded yet.');
      const payload = fromProcedureDetail({ ...procedure, version }, artifact);
      const saved = await updateArtifact(payload.artifact_id ?? procedure.artifactId, payload);
      return toProcedureDetail(saved);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-center'] });
    },
  });

  return {
    save: (procedure, version) => mutation.mutateAsync({ procedure, version }),
    isSaving: mutation.isPending,
    error: mutation.isError ? 'Unable to save this procedure.' : null,
  };
}
